import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase'; 
import { 
  collection, 
  addDoc, 
  onSnapshot, 
  query, 
  orderBy, 
  where,
  deleteDoc, 
  doc, 
  updateDoc,
  Timestamp 
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import Swal from 'sweetalert2';

export default function Nozzlemaster() {
  // Form States
  const [nozzleName, setNozzleName] = useState("");
  const [fuelType, setFuelType] = useState("MS");
  const [lastReading, setLastReading] = useState("");
  const [currentRate, setCurrentRate] = useState("");
  
  // Auth & Data States
  const [userId, setUserId] = useState(null);
  const [nozzleList, setNozzleList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Edit States
  const [editId, setEditId] = useState(null); 
  const [isEditing, setIsEditing] = useState(false);

  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
  });

  // 1. Listen for Authentication State
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
        setNozzleList([]);
      }
      setAuthLoading(false);
    });
    return () => unsubscribeAuth();
  }, []);

  // 2. Real-time Data Listener (Filtered by UserID)
  useEffect(() => {
    if (!userId) return;
    
    const q = query(
      collection(db, "nozzles"), 
      where("userId", "==", userId),
      orderBy("nozzleName", "asc")
    );

    const unsubscribeData = onSnapshot(q, (snapshot) => {
      const arr = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setNozzleList(arr);
    }, (error) => {
      console.error("Firestore Error:", error);
      if (error.code === 'failed-precondition') {
        console.warn("Index required. Check the link in the console above.");
      }
    });

    return () => unsubscribeData();
  }, [userId]);

  // 3. Handle Submit (Add/Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!userId) {
      return Toast.fire({ icon: 'error', title: 'Session expired. Please login.' });
    }
    
    if (!nozzleName ) {
      return Toast.fire({ icon: 'warning', title: 'Please fill all fields' });
    }

    setLoading(true);
    try {
      const nozzleData = {
        nozzleName: nozzleName.toUpperCase(),
        fuelType: fuelType,
        lastReading: parseFloat(lastReading),
        currentRate: parseFloat(currentRate),
        updatedAt: Timestamp.now(),
        userId: userId // Essential for security rules
      };

      if (isEditing) {
        await updateDoc(doc(db, "nozzles", editId), nozzleData);
        Toast.fire({ icon: 'success', title: 'Nozzle Updated' });
      } else {
        await addDoc(collection(db, "nozzles"), {
          ...nozzleData,
          created: Timestamp.now()
        });
        Toast.fire({ icon: 'success', title: 'Nozzle Added' });
      }

      resetForm();
    } catch (error) {
      Toast.fire({ icon: 'error', title: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (nozzle) => {
    setIsEditing(true);
    setEditId(nozzle.id);
    setNozzleName(nozzle.nozzleName);
    setFuelType(nozzle.fuelType);
    setLastReading(nozzle.lastReading);
    setCurrentRate(nozzle.currentRate);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setIsEditing(false);
    setEditId(null);
    setNozzleName("");
    setLastReading("");
    setCurrentRate("");
    setFuelType("MS");
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Nozzle?',
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, "nozzles", id));
        Toast.fire({ icon: 'success', title: 'Deleted!' });
      } catch (err) {
        Toast.fire({ icon: 'error', title: 'Delete failed' });
      }
    }
  };

  // Loading Screen for Auth
  if (authLoading) {
    return (
      <div className="content-wrapper d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <div className="spinner-border text-danger" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  // Redirect/Gate if not logged in
  if (!userId) {
    return (
      <div className="content-wrapper text-center p-5">
        <div className="alert alert-info">
          <h5><i className="icon fas fa-info"></i> Access Denied</h5>
          Please sign in to view and manage your pump nozzles.
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Pump Nozzle Master</h1>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            
            {/* FORM SECTION */}
            <div className="col-md-4">
              <div className={`card ${isEditing ? 'card-warning' : 'card-danger'} shadow`}>
                <div className="card-header">
                  <h3 className="card-title">{isEditing ? 'Edit Nozzle' : 'Add New Nozzle'}</h3>
                  {isEditing && (
                    <button className="btn btn-xs btn-light float-right" onClick={resetForm}>Cancel</button>
                  )}
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="card-body">
                    <div className="form-group">
                      <label>Nozzle Name</label>
                      <input type="text" className="form-control" value={nozzleName} 
                        onChange={(e) => setNozzleName(e.target.value)} placeholder="e.g. N1" />
                    </div>
                    
                    <div className="form-group">
                      <label>Fuel Type</label>
                      <select className="form-control" value={fuelType} onChange={(e) => setFuelType(e.target.value)}>
                        <option value="MS">MS (Petrol)</option>
                        <option value="HSD">HSD (Diesel)</option>
                        <option value="POWER">Power/Speed</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Opening Meter Reading</label>
                      <input type="number" step="0.01" className="form-control" value={lastReading} 
                        onChange={(e) => setLastReading(e.target.value)} placeholder="0.00" />
                    </div>

                    <div className="form-group">
                      <label>Selling Price (₹)</label>
                      <input type="number" step="0.01" className="form-control" value={currentRate} 
                        onChange={(e) => setCurrentRate(e.target.value)} placeholder="0.00" />
                    </div>
                  </div>
                  <div className="card-footer">
                    <button type="submit" disabled={loading} className={`btn btn-block ${isEditing ? 'btn-warning' : 'btn-danger'}`}>
                      <i className="fas fa-save mr-2"></i>
                      {loading ? 'Processing...' : isEditing ? 'UPDATE NOZZLE' : 'SAVE NOZZLE'}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* LIST SECTION */}
            <div className="col-md-8">
              <div className="card shadow">
                <div className="card-header border-transparent">
                  <h3 className="card-title">Configured Nozzles</h3>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table m-0 table-hover table-striped">
                      <thead className="thead-light">
                        <tr>
                          <th>Nozzle</th>
                          <th>Fuel</th>
                          <th>Current Reading</th>
                          <th>Rate</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {nozzleList.map((nozzle) => (
                          <tr key={nozzle.id}>
                            <td><strong>{nozzle.nozzleName}</strong></td>
                            <td>
                              <span className={`badge ${nozzle.fuelType === 'MS' ? 'badge-success' : nozzle.fuelType === 'HSD' ? 'badge-primary' : 'badge-warning'}`}>
                                {nozzle.fuelType}
                              </span>
                            </td>
                            <td>{nozzle.lastReading?.toLocaleString()}</td>
                            <td>₹ {nozzle.currentRate}</td>
                            <td>
                              <button onClick={() => handleEdit(nozzle)} className="btn btn-sm btn-info mr-2">
                                <i className="fas fa-edit"></i>
                              </button>
                              <button onClick={() => handleDelete(nozzle.id)} className="btn btn-sm btn-danger">
                                <i className="fas fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                        {nozzleList.length === 0 && (
                          <tr>
                            <td colSpan="5" className="text-center py-4 text-muted">No nozzles registered yet.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
